import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { directUploadToStorage } from "@/lib/storageUpload";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { Loader2, Car, Camera, CreditCard, Upload, CheckCircle, AlertTriangle, Clock, ImageIcon, FileText, Gauge, TrendingUp, Calendar, Trash2, Edit } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { format, startOfMonth, endOfMonth, subMonths, startOfQuarter, getQuarter } from "date-fns";
import { de } from "date-fns/locale";
interface EmployeeVehicleTasksTabProps {
  employeeId: string;
  employeeName: string;
}
interface Vehicle {
  id: string;
  name: string;
  license_plate: string | null;
  manufacturer: string | null;
}
interface VehicleTask {
  id: string;
  task_type: string;
  due_date: string;
  completed_at: string | null;
  vehicle_id: string | null;
  notes: string | null;
}
interface OdometerReading {
  id: string;
  reading_date: string;
  odometer_value: number;
  photo_url: string | null;
  kilometers_this_month: number | null;
  kilometers_cumulative: number | null;
  notes: string | null;
}
interface VehiclePhoto {
  id: string;
  photo_type: string;
  photo_url: string;
  description: string | null;
  damage_location: string | null;
  damage_severity: string | null;
  reported_at: string;
}
interface LicenseVerification {
  id: string;
  verification_date: string;
  photo_url: string;
  quarter: string;
  year: number;
}
export function EmployeeVehicleTasksTab({
  employeeId,
  employeeName
}: EmployeeVehicleTasksTabProps) {
  const {
    user,
    userRole
  } = useAuth();
  const isAdmin = userRole === 'admin' || userRole === 'hr_manager';
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [tasks, setTasks] = useState<VehicleTask[]>([]);
  const [odometerReadings, setOdometerReadings] = useState<OdometerReading[]>([]);
  const [vehiclePhotos, setVehiclePhotos] = useState<VehiclePhoto[]>([]);
  const [licenseVerifications, setLicenseVerifications] = useState<LicenseVerification[]>([]);
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [odometerDialogOpen, setOdometerDialogOpen] = useState(false);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [licenseDialogOpen, setLicenseDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Edit dialog states
  const [editOdometerDialogOpen, setEditOdometerDialogOpen] = useState(false);
  const [editingOdometer, setEditingOdometer] = useState<OdometerReading | null>(null);
  const [editOdometerValue, setEditOdometerValue] = useState("");
  const [editOdometerNotes, setEditOdometerNotes] = useState("");

  // AI analysis states
  const [analyzingOdometer, setAnalyzingOdometer] = useState(false);
  const [suggestedOdometerValue, setSuggestedOdometerValue] = useState<number | null>(null);
  const [aiConfidence, setAiConfidence] = useState<string | null>(null);
  const [aiMessage, setAiMessage] = useState<string | null>(null);

  // Form states
  const [odometerValue, setOdometerValue] = useState("");
  const [odometerPhoto, setOdometerPhoto] = useState<File | null>(null);
  const [odometerPhotoPreview, setOdometerPhotoPreview] = useState<string | null>(null);
  const [odometerNotes, setOdometerNotes] = useState("");
  const [vehiclePhotoFile, setVehiclePhotoFile] = useState<File | null>(null);
  const [photoType, setPhotoType] = useState<string>("interior");
  const [photoDescription, setPhotoDescription] = useState("");
  const [damageLocation, setDamageLocation] = useState("");
  const [damageSeverity, setDamageSeverity] = useState<string>("");
  const [licensePhoto, setLicensePhoto] = useState<File | null>(null);

  // Refs for camera/file inputs
  const odometerInputRef = useRef<HTMLInputElement>(null);
  const vehiclePhotoInputRef = useRef<HTMLInputElement>(null);
  const licenseInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (user && employeeId) {
      loadData();
    }
  }, [user, employeeId]);
  const loadData = async () => {
    try {
      // Load assigned vehicle
      const {
        data: vehicleData
      } = await supabase.from("vehicles").select("id, name, license_plate, manufacturer").eq("responsible_employee_id", employeeId).maybeSingle();
      setVehicle(vehicleData);

      // Load tasks
      const {
        data: tasksData
      } = await supabase.from("employee_vehicle_tasks").select("*").eq("employee_id", employeeId).order("due_date", {
        ascending: false
      });
      setTasks(tasksData || []);
      if (vehicleData) {
        // Load odometer readings
        const {
          data: odometerData
        } = await supabase.from("vehicle_odometer_readings").select("*").eq("vehicle_id", vehicleData.id).eq("employee_id", employeeId).order("reading_date", {
          ascending: false
        });
        setOdometerReadings(odometerData || []);

        // Load vehicle photos
        const {
          data: photosData
        } = await supabase.from("vehicle_photos").select("*").eq("vehicle_id", vehicleData.id).eq("employee_id", employeeId).order("created_at", {
          ascending: false
        });
        setVehiclePhotos(photosData || []);
      }

      // Load license verifications
      const {
        data: licenseData
      } = await supabase.from("driver_license_verifications").select("*").eq("employee_id", employeeId).order("verification_date", {
        ascending: false
      });
      setLicenseVerifications(licenseData || []);
    } catch (error) {
      console.error("Error loading data:", error);
      toast({
        title: "Fehler",
        description: "Daten konnten nicht geladen werden.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Admin delete handlers
  const handleDeleteOdometerReading = async (readingId: string) => {
    try {
      const {
        error
      } = await supabase.from("vehicle_odometer_readings").delete().eq("id", readingId);
      if (error) throw error;
      setOdometerReadings(prev => prev.filter(r => r.id !== readingId));
      toast({
        title: "Erfolg",
        description: "Kilometerstand gelöscht."
      });
    } catch (error) {
      console.error("Error deleting odometer reading:", error);
      toast({
        title: "Fehler",
        description: "Löschen fehlgeschlagen.",
        variant: "destructive"
      });
    }
  };
  const handleDeleteVehiclePhoto = async (photoId: string) => {
    try {
      const {
        error
      } = await supabase.from("vehicle_photos").delete().eq("id", photoId);
      if (error) throw error;
      setVehiclePhotos(prev => prev.filter(p => p.id !== photoId));
      toast({
        title: "Erfolg",
        description: "Fahrzeugfoto gelöscht."
      });
    } catch (error) {
      console.error("Error deleting vehicle photo:", error);
      toast({
        title: "Fehler",
        description: "Löschen fehlgeschlagen.",
        variant: "destructive"
      });
    }
  };
  const handleDeleteLicenseVerification = async (verificationId: string) => {
    try {
      const {
        error
      } = await supabase.from("driver_license_verifications").delete().eq("id", verificationId);
      if (error) throw error;
      setLicenseVerifications(prev => prev.filter(v => v.id !== verificationId));
      toast({
        title: "Erfolg",
        description: "Führerscheinkontrolle gelöscht."
      });
    } catch (error) {
      console.error("Error deleting license verification:", error);
      toast({
        title: "Fehler",
        description: "Löschen fehlgeschlagen.",
        variant: "destructive"
      });
    }
  };

  // Admin edit handlers
  const openEditOdometerDialog = (reading: OdometerReading) => {
    setEditingOdometer(reading);
    setEditOdometerValue(reading.odometer_value.toString());
    setEditOdometerNotes(reading.notes || "");
    setEditOdometerDialogOpen(true);
  };
  const handleSaveOdometerEdit = async () => {
    if (!editingOdometer) return;
    try {
      setUploading(true);
      const newValue = parseInt(editOdometerValue);
      if (isNaN(newValue)) {
        toast({
          title: "Fehler",
          description: "Bitte gültigen Kilometerwert eingeben.",
          variant: "destructive"
        });
        return;
      }
      const {
        error
      } = await supabase.from("vehicle_odometer_readings").update({
        odometer_value: newValue,
        notes: editOdometerNotes || null,
        updated_at: new Date().toISOString()
      }).eq("id", editingOdometer.id);
      if (error) throw error;
      setOdometerReadings(prev => prev.map(r => r.id === editingOdometer.id ? {
        ...r,
        odometer_value: newValue,
        notes: editOdometerNotes || null
      } : r));
      setEditOdometerDialogOpen(false);
      setEditingOdometer(null);
      toast({
        title: "Erfolg",
        description: "Kilometerstand aktualisiert."
      });
    } catch (error) {
      console.error("Error updating odometer reading:", error);
      toast({
        title: "Fehler",
        description: "Aktualisieren fehlgeschlagen.",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };
  const uploadFile = async (file: File, folder: string): Promise<string | null> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
    const filePath = `${employeeId}/${folder}/${fileName}`;
    try {
      // Use the public employee-profiles bucket for vehicle-related uploads
      await directUploadToStorage({
        bucket: "employee-profiles",
        path: filePath,
        file: file
      });
      const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
      return `${SUPABASE_URL}/storage/v1/object/public/employee-profiles/${filePath}`;
    } catch (error) {
      console.error("Upload error:", error);
      return null;
    }
  };
  const analyzeOdometerPhoto = async (file: File) => {
    setAnalyzingOdometer(true);
    setSuggestedOdometerValue(null);
    setAiConfidence(null);
    setAiMessage(null);
    try {
      // Convert file to base64
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const imageBase64 = await base64Promise;
      console.log('Sending image for AI analysis...');
      const {
        data,
        error
      } = await supabase.functions.invoke('analyze-odometer', {
        body: {
          imageBase64
        }
      });
      if (error) {
        console.error('AI analysis error:', error);
        toast({
          title: "KI-Analyse fehlgeschlagen",
          description: "Der Kilometerstand konnte nicht automatisch erkannt werden. Bitte geben Sie den Wert manuell ein.",
          variant: "destructive"
        });
        return;
      }
      console.log('AI analysis result:', data);
      if (data.success && data.odometer_value) {
        setSuggestedOdometerValue(data.odometer_value);
        setAiConfidence(data.confidence);
        setAiMessage(data.message);
        toast({
          title: "KI-Vorschlag",
          description: `Erkannter Kilometerstand: ${data.odometer_value.toLocaleString('de-DE')} km (${data.confidence === 'high' ? 'hohe' : data.confidence === 'medium' ? 'mittlere' : 'niedrige'} Konfidenz)`
        });
      } else {
        setAiMessage(data.message || 'Kilometerstand konnte nicht erkannt werden.');
        toast({
          title: "Keine Erkennung",
          description: data.message || "Der Kilometerstand konnte nicht automatisch erkannt werden.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error analyzing odometer:', error);
      toast({
        title: "Fehler",
        description: "Die KI-Analyse ist fehlgeschlagen.",
        variant: "destructive"
      });
    } finally {
      setAnalyzingOdometer(false);
    }
  };
  const handleOdometerPhotoChange = async (file: File) => {
    setOdometerPhoto(file);

    // Create preview URL
    const reader = new FileReader();
    reader.onload = () => {
      setOdometerPhotoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
    await analyzeOdometerPhoto(file);
  };
  const acceptSuggestedValue = () => {
    if (suggestedOdometerValue) {
      setOdometerValue(suggestedOdometerValue.toString());
      toast({
        title: "Übernommen",
        description: "Der vorgeschlagene Kilometerstand wurde übernommen."
      });
    }
  };
  const saveDocumentRecord = async (fileName: string, filePath: string, fileSize: number, mimeType: string, category: string, description: string): Promise<string | null> => {
    const {
      data,
      error
    } = await supabase.from("employee_documents").insert({
      employee_id: employeeId,
      file_name: fileName,
      file_path: filePath,
      file_size: fileSize,
      mime_type: mimeType,
      category: category,
      description: description,
      uploaded_by: user?.id
    }).select("id").single();
    if (error) {
      console.error("Error saving document record:", error);
      return null;
    }
    return data.id;
  };
  const handleOdometerSubmit = async () => {
    if (!odometerValue || !vehicle) {
      toast({
        title: "Fehler",
        description: "Bitte geben Sie den Kilometerstand ein.",
        variant: "destructive"
      });
      return;
    }
    setUploading(true);
    try {
      let photoUrl: string | null = null;
      let documentId: string | null = null;

      // Only upload photo if one was provided
      if (odometerPhoto) {
        photoUrl = await uploadFile(odometerPhoto, "odometer");
        if (!photoUrl) throw new Error("Foto-Upload fehlgeschlagen");

        // Save document record
        documentId = await saveDocumentRecord(odometerPhoto.name, photoUrl, odometerPhoto.size, odometerPhoto.type, "kfz_bilder", `Kilometerstand ${odometerValue} km - ${format(new Date(), "dd.MM.yyyy")}`);
      }

      // Calculate monthly and cumulative kilometers
      const currentValue = parseInt(odometerValue);
      let kilometersThisMonth = null;
      let kilometersCumulative = null;
      if (odometerReadings.length > 0) {
        const lastReading = odometerReadings[0];
        kilometersThisMonth = currentValue - lastReading.odometer_value;

        // Calculate cumulative from the first reading
        const firstReading = odometerReadings[odometerReadings.length - 1];
        kilometersCumulative = currentValue - firstReading.odometer_value;
      }

      // Save odometer reading
      const {
        error
      } = await supabase.from("vehicle_odometer_readings").insert({
        employee_id: employeeId,
        vehicle_id: vehicle.id,
        odometer_value: currentValue,
        photo_url: photoUrl,
        photo_document_id: documentId,
        kilometers_this_month: kilometersThisMonth,
        kilometers_cumulative: kilometersCumulative,
        notes: odometerNotes || null,
        created_by: user?.id
      });
      if (error) throw error;

      // Mark task as completed
      const currentMonth = format(new Date(), "yyyy-MM");
      await supabase.from("employee_vehicle_tasks").update({
        completed_at: new Date().toISOString(),
        completed_by: user?.id
      }).eq("employee_id", employeeId).eq("task_type", "odometer_reading").gte("due_date", startOfMonth(new Date()).toISOString()).lte("due_date", endOfMonth(new Date()).toISOString()).is("completed_at", null);
      toast({
        title: "Erfolg",
        description: "Kilometerstand wurde erfolgreich gespeichert."
      });

      // Reset form
      setOdometerValue("");
      setOdometerPhoto(null);
      setOdometerPhotoPreview(null);
      setOdometerNotes("");
      setSuggestedOdometerValue(null);
      setAiConfidence(null);
      setAiMessage(null);
      setOdometerDialogOpen(false);
      loadData();
    } catch (error) {
      console.error("Error submitting odometer:", error);
      toast({
        title: "Fehler",
        description: "Kilometerstand konnte nicht gespeichert werden.",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };
  const handleVehiclePhotoSubmit = async () => {
    if (!vehiclePhotoFile || !vehicle) {
      toast({
        title: "Fehler",
        description: "Bitte wählen Sie ein Foto aus.",
        variant: "destructive"
      });
      return;
    }
    if (photoType === "damage" && !damageLocation) {
      toast({
        title: "Fehler",
        description: "Bitte geben Sie den Ort des Schadens an.",
        variant: "destructive"
      });
      return;
    }
    setUploading(true);
    try {
      // Upload photo
      const photoUrl = await uploadFile(vehiclePhotoFile, "vehicle_photos");
      if (!photoUrl) throw new Error("Foto-Upload fehlgeschlagen");

      // Save document record
      const category = photoType === "damage" ? "kfz_schaden" : "kfz_bilder";
      const description = photoType === "damage" ? `Schadensfoto: ${damageLocation} - ${damageSeverity || "Unbekannt"}` : `Fahrzeugfoto: ${photoType === "interior" ? "Innenraum" : "Außen"}`;
      await saveDocumentRecord(vehiclePhotoFile.name, photoUrl, vehiclePhotoFile.size, vehiclePhotoFile.type, category, photoDescription || description);

      // Save vehicle photo record
      const {
        error
      } = await supabase.from("vehicle_photos").insert({
        employee_id: employeeId,
        vehicle_id: vehicle.id,
        photo_type: photoType,
        photo_url: photoUrl,
        description: photoDescription || null,
        damage_location: photoType === "damage" ? damageLocation : null,
        damage_severity: photoType === "damage" ? damageSeverity || null : null,
        created_by: user?.id
      });
      if (error) throw error;

      // Mark interior_photo task as completed if it's an interior photo
      if (photoType === "interior") {
        await supabase.from("employee_vehicle_tasks").update({
          completed_at: new Date().toISOString(),
          completed_by: user?.id
        }).eq("employee_id", employeeId).eq("task_type", "interior_photo").gte("due_date", startOfMonth(new Date()).toISOString()).lte("due_date", endOfMonth(new Date()).toISOString()).is("completed_at", null);
      }
      toast({
        title: "Erfolg",
        description: "Foto wurde erfolgreich hochgeladen."
      });

      // Reset form
      setVehiclePhotoFile(null);
      setPhotoType("interior");
      setPhotoDescription("");
      setDamageLocation("");
      setDamageSeverity("");
      setPhotoDialogOpen(false);
      loadData();
    } catch (error) {
      console.error("Error submitting photo:", error);
      toast({
        title: "Fehler",
        description: "Foto konnte nicht hochgeladen werden.",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };
  const handleLicenseSubmit = async () => {
    if (!licensePhoto) {
      toast({
        title: "Fehler",
        description: "Bitte machen Sie ein Foto Ihres Führerscheins.",
        variant: "destructive"
      });
      return;
    }
    setUploading(true);
    try {
      // Upload photo
      const photoUrl = await uploadFile(licensePhoto, "license_verification");
      if (!photoUrl) throw new Error("Foto-Upload fehlgeschlagen");
      const currentDate = new Date();
      const quarter = `Q${getQuarter(currentDate)}`;
      const year = currentDate.getFullYear();

      // Save document record
      await saveDocumentRecord(licensePhoto.name, photoUrl, licensePhoto.size, licensePhoto.type, "fuhrerschein", `Führerscheinkontrolle ${quarter}/${year}`);

      // Save license verification
      const {
        error
      } = await supabase.from("driver_license_verifications").insert({
        employee_id: employeeId,
        photo_url: photoUrl,
        quarter: quarter,
        year: year,
        created_by: user?.id
      });
      if (error) throw error;

      // Mark task as completed
      await supabase.from("employee_vehicle_tasks").update({
        completed_at: new Date().toISOString(),
        completed_by: user?.id
      }).eq("employee_id", employeeId).eq("task_type", "license_verification").gte("due_date", startOfQuarter(new Date()).toISOString()).is("completed_at", null);
      toast({
        title: "Erfolg",
        description: "Führerscheinkontrolle wurde erfolgreich hochgeladen."
      });

      // Reset form
      setLicensePhoto(null);
      setLicenseDialogOpen(false);
      loadData();
    } catch (error) {
      console.error("Error submitting license:", error);
      toast({
        title: "Fehler",
        description: "Führerscheinkontrolle konnte nicht gespeichert werden.",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };
  const getTaskStatus = (taskType: string): {
    status: "pending" | "completed" | "overdue";
    dueDate: Date | null;
  } => {
    const now = new Date();
    if (taskType === "odometer_reading") {
      // Check if this month's reading exists
      const currentMonth = format(now, "yyyy-MM");
      const hasCurrentMonthReading = odometerReadings.some(r => format(new Date(r.reading_date), "yyyy-MM") === currentMonth);
      if (hasCurrentMonthReading) {
        return {
          status: "completed",
          dueDate: endOfMonth(now)
        };
      }
      const dueDate = endOfMonth(now);
      return {
        status: now > dueDate ? "overdue" : "pending",
        dueDate
      };
    }
    if (taskType === "interior_photo") {
      // Check if this month's interior photo exists
      const currentMonth = format(now, "yyyy-MM");
      const hasCurrentMonthPhoto = vehiclePhotos.some(p => p.photo_type === "interior" && format(new Date(p.reported_at), "yyyy-MM") === currentMonth);
      if (hasCurrentMonthPhoto) {
        return {
          status: "completed",
          dueDate: endOfMonth(now)
        };
      }
      const dueDate = endOfMonth(now);
      return {
        status: now > dueDate ? "overdue" : "pending",
        dueDate
      };
    }
    if (taskType === "license_verification") {
      const currentQuarter = getQuarter(now);
      const currentYear = now.getFullYear();
      const hasCurrentQuarterVerification = licenseVerifications.some(v => v.quarter === `Q${currentQuarter}` && v.year === currentYear);
      if (hasCurrentQuarterVerification) {
        return {
          status: "completed",
          dueDate: null
        };
      }

      // Due at end of quarter's first month
      const quarterEndMonth = currentQuarter * 3;
      const dueDate = new Date(currentYear, quarterEndMonth - 1, 15);
      return {
        status: now > dueDate ? "overdue" : "pending",
        dueDate
      };
    }
    return {
      status: "pending",
      dueDate: null
    };
  };
  if (loading) {
    return <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>;
  }
  const odometerStatus = getTaskStatus("odometer_reading");
  const interiorPhotoStatus = getTaskStatus("interior_photo");
  const licenseStatus = getTaskStatus("license_verification");

  // Calculate statistics
  const totalKilometers = odometerReadings.length > 1 ? odometerReadings[0].odometer_value - odometerReadings[odometerReadings.length - 1].odometer_value : 0;
  const lastMonthKilometers = odometerReadings[0]?.kilometers_this_month || 0;
  return <div className="space-y-6">
      {/* Tasks Overview */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Car className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle>Fahrzeug-Aufgaben</CardTitle>
              <CardDescription>
                {vehicle ? `${vehicle.name} ${vehicle.license_plate ? `(${vehicle.license_plate})` : ''}` : 'Kein Fahrzeug zugewiesen'}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {/* Odometer Reading Task */}
            <Card className={`border-2 ${odometerStatus.status === "completed" ? "border-green-500/50 bg-green-50/50 dark:bg-green-950/20" : odometerStatus.status === "overdue" ? "border-destructive/50 bg-destructive/10" : "border-orange-500/50 bg-orange-50/50 dark:bg-orange-950/20"}`}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="p-2 bg-background rounded-lg">
                    <Gauge className="h-6 w-6" />
                  </div>
                  <Badge variant={odometerStatus.status === "completed" ? "default" : odometerStatus.status === "overdue" ? "destructive" : "secondary"}>
                    {odometerStatus.status === "completed" ? <><CheckCircle className="h-3 w-3 mr-1" /> Erledigt</> : odometerStatus.status === "overdue" ? <><AlertTriangle className="h-3 w-3 mr-1" /> Überfällig</> : <><Clock className="h-3 w-3 mr-1" /> Ausstehend</>}
                  </Badge>
                </div>
                <h3 className="font-semibold mb-1">Kilometerstand</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Monatliche Erfassung
                </p>
                {odometerStatus.dueDate && odometerStatus.status !== "completed" && <p className="text-xs text-muted-foreground mb-4">
                    <Calendar className="h-3 w-3 inline mr-1" />
                    Fällig: {format(odometerStatus.dueDate, "dd.MM.yyyy", {
                  locale: de
                })}
                  </p>}
                <Dialog open={odometerDialogOpen} onOpenChange={setOdometerDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="w-full" size="sm" disabled={!vehicle || odometerStatus.status === "completed"}>
                      <Camera className="h-4 w-4 mr-2" />
                      Foto  oder Taochstand eingeben   
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Kilometerstand erfassen</DialogTitle>
                      <DialogDescription>
                        Geben Sie den Kilometerstand ein. Ein Foto ist optional.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Foto des Tachostands (optional)</Label>
                        <input ref={odometerInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => {
                        if (e.target.files?.[0]) {
                          handleOdometerPhotoChange(e.target.files[0]);
                        }
                      }} />
                        <div className="mt-2 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => odometerInputRef.current?.click()}>
                          {analyzingOdometer ? <div className="flex flex-col items-center gap-2">
                              <Loader2 className="h-8 w-8 animate-spin text-primary" />
                              <p className="text-sm text-muted-foreground">
                                KI analysiert Tachostand...
                              </p>
                            </div> : odometerPhotoPreview ? <div className="space-y-2">
                              <img src={odometerPhotoPreview} alt="Tacho-Vorschau" className="w-full max-h-48 object-contain rounded-lg" />
                              <p className="text-xs text-muted-foreground">Tippen um neues Foto aufzunehmen</p>
                            </div> : <>
                              <Camera className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                              <p className="text-sm text-muted-foreground">
                                Tippen um Kamera zu öffnen
                              </p>
                            </>}
                        </div>
                      </div>

                      {/* AI Suggestion Section */}
                      {suggestedOdometerValue && <div className="p-4 border rounded-lg bg-primary/5 border-primary/20">
                          <div className="flex items-center gap-2 mb-2">
                            <Gauge className="h-5 w-5 text-primary" />
                            <span className="font-medium">KI-Vorschlag</span>
                            <Badge variant={aiConfidence === 'high' ? 'default' : aiConfidence === 'medium' ? 'secondary' : 'outline'}>
                              {aiConfidence === 'high' ? 'Hohe Konfidenz' : aiConfidence === 'medium' ? 'Mittlere Konfidenz' : 'Niedrige Konfidenz'}
                            </Badge>
                          </div>
                          <p className="text-2xl font-bold mb-2">
                            {suggestedOdometerValue.toLocaleString('de-DE')} km
                          </p>
                          {aiMessage && <p className="text-sm text-muted-foreground mb-3">{aiMessage}</p>}
                          <div className="flex gap-2">
                            <Button size="sm" onClick={acceptSuggestedValue} className="flex-1">
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Übernehmen
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => {
                          setSuggestedOdometerValue(null);
                          setAiConfidence(null);
                          setAiMessage(null);
                        }}>
                              Ablehnen
                            </Button>
                          </div>
                        </div>}

                      <div>
                        <Label htmlFor="odometer-value">Kilometerstand *</Label>
                        <Input id="odometer-value" type="number" placeholder="z.B. 45230" value={odometerValue} onChange={e => setOdometerValue(e.target.value)} />
                        {suggestedOdometerValue && odometerValue && parseInt(odometerValue) !== suggestedOdometerValue && <p className="text-xs text-muted-foreground mt-1">
                            Manuell eingegeben (KI-Vorschlag: {suggestedOdometerValue.toLocaleString('de-DE')} km)
                          </p>}
                      </div>

                      <div>
                        <Label htmlFor="odometer-notes">Notizen (optional)</Label>
                        <Textarea id="odometer-notes" placeholder="z.B. Lange Dienstreise" value={odometerNotes} onChange={e => setOdometerNotes(e.target.value)} />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setOdometerDialogOpen(false)}>
                        Abbrechen
                      </Button>
                      <Button onClick={handleOdometerSubmit} disabled={uploading || analyzingOdometer}>
                        {uploading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Speichern
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>

            {/* Vehicle Photo Task */}
            <Card className={`border-2 ${interiorPhotoStatus.status === "completed" ? "border-green-500/50 bg-green-50/50 dark:bg-green-950/20" : interiorPhotoStatus.status === "overdue" ? "border-destructive/50 bg-destructive/10" : "border-orange-500/50 bg-orange-50/50 dark:bg-orange-950/20"}`}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="p-2 bg-background rounded-lg">
                    <ImageIcon className="h-6 w-6" />
                  </div>
                  <Badge variant={interiorPhotoStatus.status === "completed" ? "default" : interiorPhotoStatus.status === "overdue" ? "destructive" : "secondary"}>
                    {interiorPhotoStatus.status === "completed" ? <><CheckCircle className="h-3 w-3 mr-1" /> Erledigt</> : interiorPhotoStatus.status === "overdue" ? <><AlertTriangle className="h-3 w-3 mr-1" /> Überfällig</> : <><Clock className="h-3 w-3 mr-1" /> Ausstehend</>}
                  </Badge>
                </div>
                <h3 className="font-semibold mb-1">Fahrzeugfotos</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Monatliche Erfassung (Innenraum)
                </p>
                {interiorPhotoStatus.dueDate && interiorPhotoStatus.status !== "completed" && <p className="text-xs text-muted-foreground mb-4">
                    <Calendar className="h-3 w-3 inline mr-1" />
                    Fällig: {format(interiorPhotoStatus.dueDate, "dd.MM.yyyy", {
                  locale: de
                })}
                  </p>}
                <Dialog open={photoDialogOpen} onOpenChange={setPhotoDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="w-full" size="sm" disabled={!vehicle}>
                      <Camera className="h-4 w-4 mr-2" />
                      Foto aufnehmen
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Fahrzeugfoto hochladen</DialogTitle>
                      <DialogDescription>
                        Laden Sie ein Foto des Innenraums oder eines Schadens hoch.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Fototyp *</Label>
                        <Select value={photoType} onValueChange={setPhotoType}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="interior">Innenraum</SelectItem>
                            <SelectItem value="exterior">Außenansicht</SelectItem>
                            <SelectItem value="damage">Schaden</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {photoType === "damage" && <>
                          <div>
                            <Label htmlFor="damage-location">Schadensort *</Label>
                            <Input id="damage-location" placeholder="z.B. Vorderer Kotflügel rechts" value={damageLocation} onChange={e => setDamageLocation(e.target.value)} />
                          </div>
                          <div>
                            <Label>Schweregrad</Label>
                            <Select value={damageSeverity} onValueChange={setDamageSeverity}>
                              <SelectTrigger>
                                <SelectValue placeholder="Auswählen..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="minor">Gering (Kratzer)</SelectItem>
                                <SelectItem value="moderate">Mittel (Delle)</SelectItem>
                                <SelectItem value="severe">Schwer (strukturell)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </>}
                      
                      <div>
                        <Label>Foto *</Label>
                        <input ref={vehiclePhotoInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => {
                        if (e.target.files?.[0]) {
                          setVehiclePhotoFile(e.target.files[0]);
                        }
                      }} />
                        <div className="mt-2 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => vehiclePhotoInputRef.current?.click()}>
                          {vehiclePhotoFile ? <div className="flex items-center justify-center gap-2">
                              <CheckCircle className="h-5 w-5 text-green-500" />
                              <span className="text-sm">{vehiclePhotoFile.name}</span>
                            </div> : <>
                              <Camera className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                              <p className="text-sm text-muted-foreground">
                                Tippen um Kamera zu öffnen
                              </p>
                            </>}
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor="photo-description">Beschreibung (optional)</Label>
                        <Textarea id="photo-description" placeholder="Zusätzliche Informationen..." value={photoDescription} onChange={e => setPhotoDescription(e.target.value)} />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setPhotoDialogOpen(false)}>
                        Abbrechen
                      </Button>
                      <Button onClick={handleVehiclePhotoSubmit} disabled={uploading}>
                        {uploading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Hochladen
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>

            {/* License Verification Task */}
            <Card className={`border-2 ${licenseStatus.status === "completed" ? "border-green-500/50 bg-green-50/50 dark:bg-green-950/20" : licenseStatus.status === "overdue" ? "border-destructive/50 bg-destructive/10" : "border-orange-500/50 bg-orange-50/50 dark:bg-orange-950/20"}`}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="p-2 bg-background rounded-lg">
                    <CreditCard className="h-6 w-6" />
                  </div>
                  <Badge variant={licenseStatus.status === "completed" ? "default" : licenseStatus.status === "overdue" ? "destructive" : "secondary"}>
                    {licenseStatus.status === "completed" ? <><CheckCircle className="h-3 w-3 mr-1" /> Erledigt</> : licenseStatus.status === "overdue" ? <><AlertTriangle className="h-3 w-3 mr-1" /> Überfällig</> : <><Clock className="h-3 w-3 mr-1" /> Ausstehend</>}
                  </Badge>
                </div>
                <h3 className="font-semibold mb-1">Führerscheinkontrolle</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Vierteljährlich
                </p>
                {licenseStatus.dueDate && licenseStatus.status !== "completed" && <p className="text-xs text-muted-foreground mb-4">
                    <Calendar className="h-3 w-3 inline mr-1" />
                    Fällig: {format(licenseStatus.dueDate, "dd.MM.yyyy", {
                  locale: de
                })}
                  </p>}
                <Dialog open={licenseDialogOpen} onOpenChange={setLicenseDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="w-full" size="sm" disabled={licenseStatus.status === "completed"}>
                      <Camera className="h-4 w-4 mr-2" />
                      Führerschein hinzufügen
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Führerscheinkontrolle</DialogTitle>
                      <DialogDescription>
                        Fotografieren Sie Ihren Führerschein oder laden Sie ein Bild hoch.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Foto des Führerscheins *</Label>
                        {/* Camera input - opens camera on mobile */}
                        <input ref={licenseInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => {
                        if (e.target.files?.[0]) {
                          setLicensePhoto(e.target.files[0]);
                        }
                      }} />
                        {/* File upload input - for selecting existing files */}
                        <input id="license-file-upload" type="file" accept="image/*" className="hidden" onChange={e => {
                        if (e.target.files?.[0]) {
                          setLicensePhoto(e.target.files[0]);
                        }
                      }} />
                        
                        {licensePhoto ? <div className="mt-2 border-2 border-green-500 rounded-lg p-4 text-center bg-green-50 dark:bg-green-900/20">
                            <div className="flex items-center justify-center gap-2">
                              <CheckCircle className="h-5 w-5 text-green-500" />
                              <span className="text-sm font-medium">{licensePhoto.name}</span>
                            </div>
                            <Button variant="ghost" size="sm" className="mt-2 text-muted-foreground" onClick={() => setLicensePhoto(null)}>
                              Anderes Bild wählen
                            </Button>
                          </div> : <div className="mt-2 grid grid-cols-2 gap-3">
                            {/* Camera button */}
                            <div className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:bg-muted/50 hover:border-primary/50 transition-colors" onClick={() => licenseInputRef.current?.click()}>
                              <Camera className="h-8 w-8 mx-auto mb-2 text-primary" />
                              <p className="text-sm font-medium">Foto aufnehmen</p>
                              <p className="text-xs text-muted-foreground mt-1">Kamera öffnen</p>
                            </div>
                            
                            {/* File upload button */}
                            <div className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:bg-muted/50 hover:border-primary/50 transition-colors" onClick={() => document.getElementById('license-file-upload')?.click()}>
                              <Upload className="h-8 w-8 mx-auto mb-2 text-primary" />
                              <p className="text-sm font-medium">Bild hochladen</p>
                              <p className="text-xs text-muted-foreground mt-1">Datei auswählen</p>
                            </div>
                          </div>}
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setLicenseDialogOpen(false)}>
                        Abbrechen
                      </Button>
                      <Button onClick={handleLicenseSubmit} disabled={uploading || !licensePhoto}>
                        {uploading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Speichern
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Kilometer Statistics */}
      {vehicle && odometerReadings.length > 0 && <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Kilometerstatistik
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground">Aktueller Stand</p>
                <p className="text-2xl font-bold">{odometerReadings[0].odometer_value.toLocaleString()} km</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground">Letzter Monat</p>
                <p className="text-2xl font-bold">+{lastMonthKilometers.toLocaleString()} km</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground">Gesamt erfasst</p>
                <p className="text-2xl font-bold">+{totalKilometers.toLocaleString()} km</p>
              </div>
            </div>

            {/* Recent Readings with Photos */}
            <div className="mt-6">
              <h4 className="font-medium mb-3">Letzte Erfassungen</h4>
              <div className="space-y-3">
                {odometerReadings.slice(0, 5).map(reading => <div key={reading.id} className="flex items-center gap-4 p-3 bg-muted/30 rounded-lg">
                    {reading.photo_url ? <img src={reading.photo_url} alt={`Tacho ${reading.odometer_value} km`} className="w-20 h-14 object-cover rounded-lg flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => window.open(reading.photo_url!, '_blank')} /> : <div className="w-20 h-14 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
                        <Gauge className="h-6 w-6 text-muted-foreground" />
                      </div>}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">
                          {format(new Date(reading.reading_date), "dd.MM.yyyy", {
                      locale: de
                    })}
                        </span>
                        {reading.kilometers_this_month !== null && <Badge variant="secondary">
                            +{reading.kilometers_this_month.toLocaleString()} km
                          </Badge>}
                      </div>
                      <p className="font-semibold">{reading.odometer_value.toLocaleString()} km</p>
                      {reading.notes && <p className="text-xs text-muted-foreground truncate">{reading.notes}</p>}
                    </div>
                    {isAdmin && <div className="flex items-center gap-1 flex-shrink-0">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditOdometerDialog(reading)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Kilometerstand löschen?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Möchten Sie diesen Eintrag ({reading.odometer_value.toLocaleString()} km vom {format(new Date(reading.reading_date), "dd.MM.yyyy", {
                          locale: de
                        })}) wirklich löschen?
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDeleteOdometerReading(reading.id)}>
                                Löschen
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>}
                  </div>)}
              </div>
            </div>
          </CardContent>
        </Card>}

      {/* Recent Vehicle Photos */}
      {vehiclePhotos.length > 0 && <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5" />
              Letzte Fahrzeugfotos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
              {vehiclePhotos.slice(0, 6).map(photo => <div key={photo.id} className="relative group">
                  <img src={photo.photo_url} alt={photo.description || "Fahrzeugfoto"} className="w-full h-32 object-cover rounded-lg cursor-pointer" onClick={() => window.open(photo.photo_url, '_blank')} />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-end justify-between p-2">
                    <div className="text-white text-xs">
                      <Badge variant={photo.photo_type === "damage" ? "destructive" : "secondary"} className="mb-1">
                        {photo.photo_type === "interior" ? "Innenraum" : photo.photo_type === "damage" ? "Schaden" : "Außen"}
                      </Badge>
                      <p>{format(new Date(photo.reported_at), "dd.MM.yyyy", {
                    locale: de
                  })}</p>
                    </div>
                    {isAdmin && <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="destructive" size="icon" className="h-7 w-7">
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Fahrzeugfoto löschen?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Möchten Sie dieses Foto vom {format(new Date(photo.reported_at), "dd.MM.yyyy", {
                        locale: de
                      })} wirklich löschen?
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDeleteVehiclePhoto(photo.id)}>
                              Löschen
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>}
                  </div>
                </div>)}
            </div>
          </CardContent>
        </Card>}

      {/* License Verification History */}
      {licenseVerifications.length > 0 && <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Führerscheinkontrollen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {licenseVerifications.slice(0, 4).map(verification => <div key={verification.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    {verification.photo_url && <img src={verification.photo_url} alt="Führerschein" className="w-12 h-8 object-cover rounded cursor-pointer hover:opacity-80" onClick={() => window.open(verification.photo_url, '_blank')} />}
                    <CreditCard className="h-4 w-4 text-muted-foreground" />
                    <span>{verification.quarter}/{verification.year}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      {format(new Date(verification.verification_date), "dd.MM.yyyy", {
                  locale: de
                })}
                    </span>
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    {isAdmin && <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Führerscheinkontrolle löschen?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Möchten Sie diese Führerscheinkontrolle ({verification.quarter}/{verification.year}) wirklich löschen?
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDeleteLicenseVerification(verification.id)}>
                              Löschen
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>}
                  </div>
                </div>)}
            </div>
          </CardContent>
        </Card>}

      {/* Edit Odometer Dialog */}
      <Dialog open={editOdometerDialogOpen} onOpenChange={setEditOdometerDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kilometerstand bearbeiten</DialogTitle>
            <DialogDescription>
              Bearbeiten Sie den erfassten Kilometerstand.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-odometer-value">Kilometerstand *</Label>
              <Input id="edit-odometer-value" type="number" value={editOdometerValue} onChange={e => setEditOdometerValue(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="edit-odometer-notes">Notizen</Label>
              <Textarea id="edit-odometer-notes" value={editOdometerNotes} onChange={e => setEditOdometerNotes(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOdometerDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSaveOdometerEdit} disabled={uploading}>
              {uploading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>;
}
export default EmployeeVehicleTasksTab;